import React from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import { inBildpixel, normalisiere, Rahmen } from "./zuschnitt";
import { alleFenster } from "../../windowManager/browserFenster/fensterRegister";

export type AufnahmeParams = { scrollX: number; scrollY: number; breite: number; hoehe: number; dpr: number };

/**
 * Reine Optionen-Berechnung für html2canvas-pro — bewusst ohne DOM-Zugriff,
 * damit sie sich isoliert testen lässt. Aufgenommen wird genau der sichtbare
 * Ausschnitt: `x`/`y` = Scroll-Versatz, Breite/Höhe = Fenster, Pixeldichte
 * höchstens 2. `useCORS: false` — Fremdbilder ohne CORS bleiben leer, statt
 * die Aufnahme zu kippen.
 *
 * Warum nicht mehr html-to-image: Auf echten KMVS-Seiten (~1200 Knoten, große
 * <datalist>s) fror dessen SVG-foreignObject-Weg den Renderer > 60 s ein, und
 * ein Fremd-Favicon ohne CORS ließ die Aufnahme trotz Platzhalter scheitern.
 */
export function aufnahmeOptionen({ scrollX, scrollY, breite, hoehe, dpr }: AufnahmeParams) {
  return {
    x: scrollX,
    y: scrollY,
    width: breite,
    height: hoehe,
    windowWidth: breite,
    windowHeight: hoehe,
    scale: Math.min(dpr, 2),
    useCORS: false,
    logging: false,
  };
}

const NIE_MITNEHMEN = new Set(["NOSCRIPT", "SCRIPT", "TEMPLATE", "DATALIST"]);

/**
 * `ignoreElements` für html2canvas-pro: Dialog, Melden-Knopf & Co.
 * (`data-ticket-ignore`) und unsichtbare Technik-Elemente — sonst erscheint
 * z. B. der <noscript>-Text oben links im Bild.
 */
export function ignoriereElement(el: Element): boolean {
  return el.hasAttribute("data-ticket-ignore") || NIE_MITNEHMEN.has(el.tagName);
}

/** Lesbare Fehlerbeschreibung für die technischen Spuren (Events wären als JSON nur `{"isTrusted":true}`). */
export function fehlerBeschreibung(f: unknown): string {
  if (f instanceof Error) return `${f.name}: ${f.message}`;
  if (typeof Event !== "undefined" && f instanceof Event) {
    const ziel = f.target as (Element & { src?: string }) | null;
    const quelle = ziel && typeof ziel.src === "string" ? ` ${ziel.src.slice(0, 120)}` : "";
    return `Event „${f.type}“ an ${ziel?.tagName?.toLowerCase() ?? "?"}${quelle}`;
  }
  return String(f);
}

/** Sichtbaren Ausschnitt nachzeichnen; die Bibliothek wird erst hier nachgeladen (kein Ballast im Haupt-Bundle). */
export async function nimmSichtbarenAusschnitt(): Promise<HTMLCanvasElement> {
  const optionen = aufnahmeOptionen({
    scrollX: window.scrollX,
    scrollY: window.scrollY,
    breite: window.innerWidth,
    hoehe: window.innerHeight,
    dpr: window.devicePixelRatio || 1,
  });
  try {
    const { default: html2canvas } = await import("html2canvas-pro");
    return await html2canvas(document.body, { ...optionen, ignoreElements: ignoriereElement });
  } catch (fehler) {
    console.error("[Ticket-Screenshot] Aufnahme fehlgeschlagen:", fehlerBeschreibung(fehler));
    throw fehler;
  }
}

type Props = {
  onFertig: (c: HTMLCanvasElement) => void;
  onAbbruch: (fehler?: string) => void;
  /** Während der Aufnahme nichts einblenden (Selbstauslöser: Hover-Zustände sollen stehen bleiben). */
  ohneLadeHinweis?: boolean;
};

export function BereichAufnahme({ onFertig, onAbbruch, ohneLadeHinweis = false }: Props) {
  const [bild, setBild] = React.useState<HTMLCanvasElement | null>(null);
  const [start, setStart] = React.useState<{ x: number; y: number } | null>(null);
  const [rahmen, setRahmen] = React.useState<Rahmen | null>(null);

  // onAbbruch in einer Ref halten: der Effekt darf nur EINMAL beim Mount
  // aufnehmen. Stünde onAbbruch direkt in den deps und übergibt der Aufrufer
  // ein Inline-Arrow, würde der Effekt bei jedem Render neu laufen und
  // wiederholt aufnehmen.
  const onAbbruchRef = React.useRef(onAbbruch);
  React.useEffect(() => {
    onAbbruchRef.current = onAbbruch;
  });

  // rahmen ebenfalls in einer Ref mitführen: der Tasten-Effekt braucht den
  // aktuellen Wert nur beim Drücken von Enter, nicht als Dependency — sonst
  // würde der Listener bei jedem Pointermove (jede Rahmen-Änderung beim
  // Ziehen) ab- und wieder angemeldet.
  const rahmenRef = React.useRef(rahmen);
  React.useEffect(() => {
    rahmenRef.current = rahmen;
  }, [rahmen]);

  React.useEffect(() => {
    let aus = false;
    // Einen Frame warten, damit Dialog/Button (data-ticket-ignore) sicher weg sind.
    requestAnimationFrame(() => {
      nimmSichtbarenAusschnitt()
        .then((c) => { if (!aus) setBild(c); })
        .catch(() => onAbbruchRef.current("Screenshot nicht möglich, bitte ‚Datei anhängen‘ nutzen."));
    });
    return () => { aus = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const zuschneiden = React.useCallback((r: Rahmen | null) => {
    if (!bild) return;
    const faktor = bild.width / window.innerWidth;
    const px = r && r.w > 4 && r.h > 4
      ? inBildpixel(r, faktor, bild.width, bild.height)
      : { x: 0, y: 0, w: bild.width, h: bild.height };
    const out = document.createElement("canvas");
    out.width = px.w;
    out.height = px.h;
    out.getContext("2d")!.drawImage(bild, px.x, px.y, px.w, px.h, 0, 0, px.w, px.h);
    onFertig(out);
  }, [bild, onFertig]);

  React.useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onAbbruch();
      } else if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        zuschneiden(rahmenRef.current);
      }
    };
    // Capture-Phase: läuft VOR den Bubbling-Handlern eines umschließenden
    // Dialogs, damit Enter/Escape hier abgefangen werden und nicht bis zu
    // dessen Tasten-Handler durchlaufen (z. B. ein Dialog-Escape, das das
    // ganze Fenster schließt, während nur die Aufnahme abgebrochen werden soll).
    alleFenster.addEventListener("keydown", taste, true);
    return () => alleFenster.removeEventListener("keydown", taste, true);
  }, [onAbbruch, zuschneiden]);

  const bildUrl = React.useMemo(() => bild?.toDataURL(), [bild]);

  if (!bild) {
    if (ohneLadeHinweis) return null;
    return createPortal(<div className="kvp-rd tk-aufnahme tk-aufnahme--laedt" data-ticket-ignore>Aufnahme …</div>, document.body);
  }

  return createPortal(
    <div
      className="kvp-rd tk-aufnahme"
      data-ticket-ignore
      onPointerDown={(e) => { setStart({ x: e.clientX, y: e.clientY }); setRahmen(null); (e.target as Element).setPointerCapture?.(e.pointerId); }}
      onPointerMove={(e) => { if (start) setRahmen(normalisiere(start.x, start.y, e.clientX, e.clientY)); }}
      onPointerUp={() => setStart(null)}
    >
      <img className="tk-aufnahme-bild" src={bildUrl} alt="" draggable={false} />
      {rahmen && (
        <div className="tk-aufnahme-rahmen" style={{ left: rahmen.x, top: rahmen.y, width: rahmen.w, height: rahmen.h }} />
      )}
      <div className="tk-aufnahme-leiste" onPointerDown={(e) => e.stopPropagation()}>
        <span>Rahmen um den Bereich ziehen</span>
        <button type="button" className="tp-detail-btn" onClick={() => onAbbruch()}>Abbrechen</button>
        <button type="button" className="tp-detail-btn" onClick={() => zuschneiden(null)}>Ganze Seite</button>
        <button type="button" className="cd-save" disabled={!rahmen} onClick={() => zuschneiden(rahmen)}>Übernehmen</button>
      </div>
    </div>,
    document.body,
  );
}
